import React, { useState, useEffect, useRef } from "react";
import RiskForm from "../risks/RiskForm";
import Modal from "react-bootstrap/Modal";
import { updateDb } from "../services/updateDb";
import { useTranslation } from 'react-i18next';
import { useAppContext } from "../AppContext";
import { expandCategories } from "../common/helpers";
import useAxiosFetch from "../common/useAxiosFetch";

/* modal for adding, editing or importing risks */
const RiskModal = ({ riskObj, show, setRefreshData, resumeFromIndex, resumePreviousOwners }) => {
  const { currentProject, clearAlertMessage, showAlertMessage, currentOrg, closeModal, setCurrentProject } = useAppContext();

  const formRef = useRef(); // Create a ref for the form
  const { t, i18n } = useTranslation();

  const [measures, setMeasures] = useState([]);
  const [isFormModified, setIsFormModified] = useState(false);
  const isRiskTemplate = Array.isArray(riskObj);
  const [currentRiskIndex, setCurrentRiskIndex] = useState(resumeFromIndex || 0);
  const [importedRisks, setImportedRisks] = useState(isRiskTemplate ? riskObj : []);
  const [tempRiskState, setTempRiskState] = useState(isRiskTemplate ? riskObj[resumeFromIndex || 0] : riskObj);
  const [previousOwners, setPreviousOwners] = useState(resumePreviousOwners || []);
  const totalPages = isRiskTemplate ? Math.ceil(riskObj.length / 1) : 1;
  const SESSION_KEY = `riskImportSession_${currentOrg.id}`;
  const [autoSelectedProject, setAutoSelectedProject] = useState(null);
  
  // Fetch projects for auto-selection
  const { data: projects } = useAxiosFetch(`/api/projects/byOrg/${currentOrg.id}`);


  if(isRiskTemplate) {
    riskObj = importedRisks[currentRiskIndex] ? importedRisks[currentRiskIndex] : riskObj[currentRiskIndex];
  }

  const persistSession = (risks, index, owners) => {
    if (!isRiskTemplate) return;
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify({
        importedRisks: risks,
        currentRiskIndex: index,
        previousOwners: owners,
        savedAt: new Date().toISOString(),
        totalRisks: risks.length,
      }));
    } catch (e) {
      // localStorage might be unavailable
    }
  };

  const handleModalClose = (skipConfirmation = false) => {
    if (isFormModified && !skipConfirmation) {
      const confirmClose = window.confirm(t('unsaved_changes'));
      if (!confirmClose) {
        return;
      }
    }
    
    clearAlertMessage(); // Clear any alert messages, make sure to set new messages after close
    setImportedRisks([]);
    setMeasures([{ measure: "", category: [] }]);
    setTempRiskState({});
    setCurrentRiskIndex(0);
    setIsFormModified(false);
    setPreviousOwners([]);
    setAutoSelectedProject(null);
    closeModal();
  };

  // Auto-select project if only one domain exists and no project is selected
  useEffect(() => {
    if (show && projects && projects.length === 1 && !currentProject.id && !riskObj?.id) {
      const singleProject = projects[0];
      setAutoSelectedProject(singleProject);
      setCurrentProject({ id: singleProject.id, name: singleProject.name });
    }
  }, [show, projects, currentProject.id, riskObj]);

  useEffect(() => {
    if (riskObj && riskObj.measures) {
      // Expand categories (string IDs or objects) to full {value, label} for Typeahead
      const updatedMeasures = riskObj.measures.map(measure => ({
        ...measure,
        category: expandCategories(measure.category, i18n.language, currentOrg.securityStandard)
      }));

      setMeasures(updatedMeasures);
    } else {
      setMeasures([{ measure: "", category: [] }]);
    }
  }, [riskObj, show]);

  const nextPage = () => {
    if (formRef.current.checkValidity()) {
      saveCurrentRisk(tempRiskState);
      if (currentRiskIndex+1 < totalPages) {
        setTempRiskState(importedRisks[currentRiskIndex+1]);  
        setCurrentRiskIndex(currentRiskIndex => currentRiskIndex + 1);
        
        // Focus on owner field after page change
        setTimeout(() => {
          const ownerInput = document.querySelector('input[name="owner"]') || 
                            document.querySelector('.input-owner-typeahead');
          if (ownerInput) {
            ownerInput.focus();
          }
        }, 100);
      }
    }
    else {
      formRef.current.reportValidity();
    }
  };

  const prevPage = () => {
    saveCurrentRisk(tempRiskState);
    if (currentRiskIndex+1 > 1) {
      setTempRiskState(importedRisks[currentRiskIndex-1]);  
        setCurrentRiskIndex(currentRiskIndex => currentRiskIndex - 1);
      }
  };

  const saveCurrentRisk = (currentRiskData) => {
    const newImportedRisks = [...importedRisks];
    newImportedRisks[currentRiskIndex] = currentRiskData;
    newImportedRisks[currentRiskIndex].measures = measures;
    setImportedRisks(newImportedRisks);
    
    // Track owner for auto-suggestion in batch imports
    let updatedOwners = previousOwners;
    if (currentRiskData.owner && !previousOwners.includes(currentRiskData.owner)) {
      updatedOwners = [...previousOwners, currentRiskData.owner];
      setPreviousOwners(updatedOwners);
    }

    // Save session so the user can resume after an interruption
    persistSession(newImportedRisks, currentRiskIndex, updatedOwners);
  };

  const submitAllRisks = async () => {
    if (!formRef.current.checkValidity()) {
      formRef.current.reportValidity();
      return;
    }
    // save last risk
    const newImportedRisks = [...importedRisks];
    newImportedRisks[currentRiskIndex] = tempRiskState;
    newImportedRisks[currentRiskIndex].measures = measures;

    // add projectId to each risk
    newImportedRisks.forEach(risk => {
      risk.projectId = currentProject.id;
    });

    const response = await updateDb("/api/risks/batch", newImportedRisks);
    if (response.success) {
      // Handle successful batch submission
      localStorage.removeItem(SESSION_KEY);
      handleModalClose(true);

      setRefreshData(new Date().getTime());
      showAlertMessage('warning', t('risk') + " batch " + t('added'));
    } else {
      // Handle errors or unsuccessful submission
    }
  };

  const isEditing = Boolean(riskObj);

  // Determine modal title with project name
  const getModalTitle = () => {
    if (isRiskTemplate) {
      return `${t('import_risks')} - ${currentRiskIndex+1} ${t('of')} ${totalPages}`;
    }
    if (isEditing) {
      return t('update_risk');
    }
    // For adding risks, show domain name if available
    const projectName = autoSelectedProject?.name || currentProject?.name;
    if (projectName && currentProject?.id) {
      return `${t('add_risk')} ${t('in')} ${projectName}`;
    }
    return t('add_risk');
  };

  return (
    <Modal show={show} onHide={handleModalClose} size="xl" backdrop="static">
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>{getModalTitle()}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="update-score-modal">
        {(!isEditing || isRiskTemplate) && !currentProject.id ? (
          <div className="alert alert-danger" role="alert">
            <strong>{t('warning')}!</strong> {t('risk_project_warning')}
          </div>
        
        ) : (
          <RiskForm
            measures={measures}
            formRef={formRef}
            setMeasures={setMeasures}
            riskData={riskObj}
            riskId={riskObj?.id}
            handleClose={handleModalClose}
            setRefreshData={setRefreshData}
            setTempRiskState={setTempRiskState}
            submitAllRisks={submitAllRisks}
            isRiskTemplate={isRiskTemplate}
            finalRiskTemplatePage={isRiskTemplate && currentRiskIndex+1 === totalPages}
            isFormModified={isFormModified}
            setIsFormModified={setIsFormModified}
            previousOwners={previousOwners}
          /> 
        )}

        {isRiskTemplate && (
        <div className="pagination-controls">
            <button className="form-btn pagination-btn btn" onClick={prevPage} disabled={currentRiskIndex === 0}>{t('previous')}</button>
            <span >{currentRiskIndex+1} {t('of')} {totalPages}</span>
            <button className="form-btn pagination-btn  btn" onClick={nextPage} disabled={currentRiskIndex+1 === totalPages}>{t('next')}</button>
        </div>
        )}
      </Modal.Body>
  </Modal>);
};

export default RiskModal;